/**
 * Vesper User Service (Shared)
 */

import apiClient from './api.client';
import { API } from '../constants/api';
import { deduped, bust } from './dedupedFetch';
import type {
  AuthResponse,
  LoginPayload,
  RegisterPayload,
  UpdateProfilePayload,
  UpdatePrivacyPayload,
  PrivacySettings,
  UserPublicProfile,
  UserTrustScore,
  UserReliability,
  UserEventHistoryItem,
  MutualFriendProfile,
  GeoPoint,
} from '../types/user.types';

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function login(payload: LoginPayload): Promise<AuthResponse> {
  const res = await apiClient.post<{ data: AuthResponse }>(API.LOGIN, payload);
  return res.data.data;
}

export async function register(payload: RegisterPayload): Promise<AuthResponse> {
  const res = await apiClient.post<{ data: AuthResponse }>(API.REGISTER, payload);
  return res.data.data;
}

export async function refreshToken(): Promise<string> {
  const res = await apiClient.post<{ data: { token: string } }>(API.REFRESH_TOKEN);
  return res.data.data.token;
}

/**
 * Returns the public registration gate state (A11 of YC demo build, ADR-023).
 * Used by Register.tsx to decide whether to show the form, the "Sign-ups
 * paused" copy, or the invite-code variant. Public — no auth needed.
 */
export interface RegistrationStatus {
  enabled:          boolean;
  inviteCodeBypass: boolean;
}
export async function getRegistrationStatus(): Promise<RegistrationStatus> {
  const res = await apiClient.get<{ data: RegistrationStatus }>('/auth/registration-status');
  return res.data.data;
}

// ── Profile ───────────────────────────────────────────────────────────────────

export async function getMe(): Promise<UserPublicProfile> {
  return deduped('me', async () => {
    const res = await apiClient.get<{ data: UserPublicProfile }>(API.ME);
    return res.data.data;
  });
}

export async function updateMe(fields: UpdateProfilePayload): Promise<UserPublicProfile> {
  const res = await apiClient.patch<{ data: UserPublicProfile }>(API.ME, fields);
  bust('me');
  return res.data.data;
}

export async function deleteAccount(): Promise<void> {
  await apiClient.delete(API.ME);
  bust('me');
}

/**
 * Fetch the user's full data export. Returns the raw payload + the
 * server-suggested filename. Callers decide how to surface it — on web
 * we trigger a download via a synthetic <a> element; on mobile we could
 * write to the filesystem and share. Kept platform-agnostic here so this
 * file doesn't reach for `document`.
 */
export async function fetchMyDataExport(): Promise<{ data: unknown; filename: string }> {
  const res = await apiClient.get('/users/me/export');
  const disposition = (res.headers?.['content-disposition'] as string | undefined) ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? `vesper-data-${Date.now()}.json`;
  return { data: res.data, filename };
}

// ── Avatar Upload ─────────────────────────────────────────────────────────────

/**
 * Upload avatar. Accepts a FormData-compatible body.
 * Web: pass File. Mobile: pass { uri, name, type } via FormData.
 */
export async function uploadAvatar(formData: FormData): Promise<string> {
  const res = await apiClient.post<{ data: { avatarUrl: string } }>(
    API.MY_AVATAR,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  bust('me');
  return res.data.data.avatarUrl;
}

// ── Score ─────────────────────────────────────────────────────────────────────

export async function getMyScore(): Promise<UserTrustScore> {
  return deduped('me:score', async () => {
    const res = await apiClient.get<{ data: UserTrustScore }>(API.MY_SCORE);
    return res.data.data;
  });
}

// ── Reliability ──────────────────────────────────────────────────────────

export async function getMyReliability(): Promise<UserReliability> {
  // 10s TTL — reliability changes only after a flake/no-show is recorded,
  // which is on event-end timescales, not interactive timescales.
  return deduped('me:reliability', async () => {
    const res = await apiClient.get<{ data: UserReliability }>(API.MY_RELIABILITY);
    return res.data.data;
  }, 10_000);
}

// ── Score history (Sprint 1: Recent activity feed on Profile) ──────────

export type TrustScoreEventType =
  // Earned Reputation refactor — every new mutation logs as 'recompute'.
  | 'recompute'
  // Legacy delta-based types preserved for old log rows.
  | 'host_bonus' | 'attend_bonus' | 'flake_penalty' | 'cancel_penalty'
  | 'top_rating_bonus' | 'low_rating_penalty'
  | 'organizer_stepup_bonus' | 'organizer_flake_penalty'
  | 'pre_confirm_flake_penalty' | 'geo_verified_flake'
  | 'admin_adjustment';

/**
 * Semantic cause for `event_type='recompute'` rows. Drives the activity
 * feed label ("Hosted Coffee · +3 reputation"). NULL for legacy rows.
 */
export type TrustScoreReason =
  | 'event_completed'
  | 'event_cancelled'
  | 'event_flaked'
  | 'organizer_cancelled'
  | 'event_confirmed'
  | 'rating_received'
  | 'intro_accepted'
  | 'registered'
  | 'admin';

export interface TrustScoreLogEntry {
  id: string;
  userId: string;
  delta: number;
  eventType: TrustScoreEventType;
  sourceEventId: string | null;
  sourcePromptId: string | null;
  previousScore: number;
  newScore: number;
  reason: TrustScoreReason | null;
  createdAt: string;
}

export async function getMyScoreHistory(limit = 10): Promise<TrustScoreLogEntry[]> {
  const res = await apiClient.get<{ data: { history: TrustScoreLogEntry[] } }>(
    `${API.MY_SCORE_HISTORY}?limit=${limit}`,
  );
  return res.data.data.history;
}

// ── Location ──────────────────────────────────────────────────────────────────

export async function updateLocation(location: GeoPoint): Promise<void> {
  await apiClient.post(API.LOCATION, location);
}

// ── Other users ───────────────────────────────────────────────────────────────

export async function getUserById(id: string): Promise<UserPublicProfile> {
  const res = await apiClient.get<{ data: UserPublicProfile }>(API.USER_BY_ID(id));
  return res.data.data;
}

/**
 * Earned Reputation breakdown for another user. Returns null when the
 * backend signals the score is hidden (privacy opt-out, friends-only
 * profile not viewable, or investor verified-only mode).
 */
export async function getUserScore(id: string): Promise<UserTrustScore | null> {
  try {
    const res = await apiClient.get<{ data: UserTrustScore }>(API.USER_SCORE(id));
    return res.data.data;
  } catch {
    return null;
  }
}

export async function getMutualFriends(userId: string): Promise<MutualFriendProfile[]> {
  const res = await apiClient.get<{ data: MutualFriendProfile[] }>(API.MUTUAL_FRIENDS(userId));
  return res.data.data;
}

// ── Event History ─────────────────────────────────────────────────────────────

export async function getMyEventHistory(): Promise<UserEventHistoryItem[]> {
  return deduped('me:eventHistory', async () => {
    const res = await apiClient.get<{ data: UserEventHistoryItem[] }>(API.MY_EVENT_HISTORY);
    return res.data.data;
  });
}

// ── Privacy ───────────────────────────────────────────────────────────────────

export async function updatePrivacy(payload: UpdatePrivacyPayload): Promise<PrivacySettings> {
  const res = await apiClient.patch<{ data: PrivacySettings }>(API.MY_PRIVACY, payload);
  // Privacy flips trustScoreVisible on the user record — read by getMe().
  bust('me');
  return res.data.data;
}

// ── Identity Verification ─────────────────────────────────────────────────────

export async function startVerification(
  type: 'face' | 'id',
): Promise<{ verificationUrl: string } | null> {
  try {
    const res = await apiClient.post<{ data: { verificationUrl: string } }>(
      API.MY_VERIFY(type),
    );
    // verificationStatus on the user record changes — invalidate me cache.
    bust('me');
    return res.data.data;
  } catch {
    return null;
  }
}
